import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    ContourGeometricType,
    DicomContour,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiCreatedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { StructureSet } from '../../../domain/patient/structureset';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { ImageObject } from '../image/ImageObject';
import { RegionOfInterest } from './RegionOfInterest';
import { EventSubscriptions } from '~/domain/events/EventSubscriptions';
import { Grid } from '../common/Grid';
import { ViewManager } from '~/engine/view/ViewManager';

/**
 * {@link StructureSetObject} represents a collection of {@link RegionOfInterest RegionsOfInterest}.\
 * It inherits from {@link EnvironmentObject} so it has a physical appearance in the PatientSpace.
 */
export class StructureSetObject extends EnvironmentObject {
    /**
     * TODO: Is this a good idea? Atleast it makes things easy.
     *
     */
    public readonly parent: StructureSet;

    /**
     * An {@link Array} of {@link RegionOfInterest RegionsOfInterest} stored within this {@link StructureSetObject}.
     */
    private regionsOfInterest: Map<number, RegionOfInterest>;
    private _eventSubscriptions?: EventSubscriptions;

    private _selectedStructure: RegionOfInterest | undefined = undefined;
    public get selectedStructure(): RegionOfInterest | undefined {
        return this._selectedStructure;
    }

    public set selectedStructure(roi: RegionOfInterest | undefined) {
        if (!roi) {
            this._selectedStructure = undefined;
            return;
        }
        for (const roiSs of this.structures) {
            // verify that roi is in this structure set
            if (roi === roiSs) {
                this._selectedStructure = roi;
                return;
            }
        }
        throw new Error(
            `Attempt to set selectedStructure to ${roi.roiNumber} - ${roi.name}, but it is not part of this StructureSet`
        );
    }

    /**
     * Method to construct {@link StructureSetObject} directly from a {@link StructureSet}-datamodel, which is DICOM datamodel.
     * @param structureSet The DICOM datamodel from which the {@link StructureSetObject} is constructed.
     */
    public constructor(structureSet: StructureSet) {
        super();
        this.parent = structureSet;
        if (!structureSet.image) {
            throw new Error('StructureSetObject must have an image');
        }
        this.regionsOfInterest = new Map<number, RegionOfInterest>();
        for (const roi of structureSet) {
            const regionOfInterest = new RegionOfInterest(roi, this.grid);
            this.regionsOfInterest.set(roi.roiNumber, regionOfInterest);
        }

        const subscriptions = [
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                this.handleRoiPropertiesUpdated.bind(this)
            ),
            domainEventBus.subscribe(
                RoiCreatedEvent.EVENT_TYPE,
                this.handleRoiCreated.bind(this)
            ),
        ];

        this._eventSubscriptions = new EventSubscriptions(subscriptions);
    }

    public get displayName(): string {
        return this.parent.structureSetLabel;
    }

    public get sopInstanceUID(): string {
        return this.parent.dicomUid;
    }

    public get structures(): RegionOfInterest[] {
        return Array.from(this.regionsOfInterest.values());
    }

    /**
     * Ensure contour edits to WebGL models are updated to the domain and DICOM datamodels.
     */
    public saveChanges(): void {
        const structureSet = this.parent;
        if (structureSet.image) {
            const image3d = structureSet.image;
            for (const roi of this.structures) {
                // Point contours are managed via
                // RoiPointCoordinatesChangeRequestedEvent
                if (roi.isPointRoi) {
                    continue;
                }
                if (roi.hasBeenModified) {
                    const roiContours: DicomContour[] = [];
                    const contourSequence = roi.sequence;
                    for (const slice of contourSequence.contourSlices) {
                        const imageUid = image3d.getUidForSlice(
                            slice.normalPosition
                        );
                        if (!imageUid) {
                            const point = image3d.grid.center;
                            point[2] = slice.normalPosition;
                            if (
                                image3d.grid
                                    .toAxisAlignedBoundingBox()
                                    .isPointInside(point)
                            ) {
                                throw new Error(
                                    `Image UID not found for slice z: ${slice.normalPosition} mm.`
                                );
                            } else {
                                console.warn(
                                    `Contour outside image z: ${slice.normalPosition} mm. Skipping.`
                                );
                            }
                            continue;
                        }
                        for (const contour of slice.contours) {
                            const dicomContour = new DicomContour(
                                ContourGeometricType.CLOSED_PLANAR,
                                contour.vertices,
                                structureSet.dicomStructureSet.referencedSopClassUid,
                                imageUid
                            );
                            roiContours.push(dicomContour);
                        }
                    }
                    const domainRoi = structureSet.getRoiByNumber(
                        roi.roiNumber
                    );
                    if (domainRoi) {
                        domainRoi.contours = roiContours;
                    }
                    structureSet.dicomStructureSet.updateContourSequence(
                        roi.roiNumber
                    );
                }
            }
        }
    }

    public clearAllContours(roiNumber: number): void {
        for (const roi of this.structures) {
            if (roi.roiNumber === roiNumber) {
                if (roi.isPointRoi) {
                    continue;
                }
                const roiModify = roi.startModify('clearAllContours');
                roi.texture?.signedDistanceField?.clearTexture();
                roiModify.finish();
            }
        }
    }

    public clearContoursOnSlice(
        roiNumber: number,
        planeCoordinates: number,
        imgObject: ImageObject | undefined
    ): void {
        if (!imgObject) {
            throw new Error('ImageObject is undefined');
        }

        const imageGrid = imgObject.parent.grid;
        const sliceCenterWorld = imageGrid.snapPositionToSliceCenter(
            vec3.fromValues(0, 0, planeCoordinates)
        );

        for (const roi of this.structures) {
            if (roi.roiNumber === roiNumber) {
                if (roi.isPointRoi) {
                    continue;
                }
                if (!roi.texture) {
                    throw new Error('ROI texture is undefined');
                }
                // voxelCenterToWorldSpace guarantees we're at N+0.5 in texture
                // voxel space, so Math.floor reliably gives the correct texel
                const textureSliceZ = Math.floor(
                    roi.texture.textureGrid.toVoxelSpace(sliceCenterWorld)[2]
                );
                // Guard against out-of-range slice indices; treat as no-op
                const textureSliceVoxel = vec3.fromValues(0, 0, textureSliceZ);
                if (!roi.texture.textureGrid.includesVoxel(textureSliceVoxel)) {
                    continue;
                }
                const roiModify = roi.startModify('clearContoursOnSlice');
                roi.texture.signedDistanceField?.clearSlice(textureSliceZ);
                roiModify.finish();
            }
        }
    }

    public keepEveryNthSlice(
        roiNumber: number,
        keepEveryNthSliceValue: number
    ): EventHandlerResult {
        const roi = this.structures.find(
            (structure) => structure.roiNumber === roiNumber
        );
        if (roi?.isPointRoi) {
            throw new Error(
                'keepEveryNthSlice is not applicable to point ROIs'
            );
        }
        const roiModify = roi?.startModify('keepEveryNthSlice');
        const texture = roi?.texture;
        const minSlicePatientCoords = texture?.contentBoundingBox.min;
        const maxSlicePatientCoords = texture?.contentBoundingBox.max;
        const signedDistanceField = texture?.signedDistanceField;
        if (
            !texture ||
            !signedDistanceField ||
            !minSlicePatientCoords ||
            !maxSlicePatientCoords
        ) {
            throw new Error('Texture or signedDistanceField is undefined');
        }
        const allSlices = signedDistanceField.getAllSlices();
        const minSdfIndice = texture.textureGrid.toVoxelSpace(
            minSlicePatientCoords
        )[2];
        const maxSdfIndice = texture.textureGrid.toVoxelSpace(
            maxSlicePatientCoords
        )[2];
        const filteredSlices = allSlices.filter(
            (slice) =>
                slice !== minSdfIndice &&
                slice !== maxSdfIndice &&
                slice % keepEveryNthSliceValue !== 0
        );
        filteredSlices.forEach((contourSliceZ) => {
            signedDistanceField.clearSlice(contourSliceZ);
        });
        roiModify?.finish();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                texture.contentBoundingBox,
                LayerBit.CONTOUR_LAYER_BIT
            )
        );
    }

    public getRoi(roiNumber: number): RegionOfInterest | undefined {
        return this.regionsOfInterest.get(roiNumber);
    }

    // method to find the closest roi to the patientCoords
    public findRoi(
        patientCoords: ReadonlyVec3,
        threshold: number = 30
    ): RegionOfInterest | undefined {
        const rois = Array.from(this.regionsOfInterest.values());
        const visibleRois = rois.filter((roi) => roi.isVisible);
        let minDist = 9999;
        let result: RegionOfInterest | undefined = undefined;
        visibleRois.forEach((roi: RegionOfInterest) => {
            if (roi.isPointRoi) {
                const pt = roi.pointCoordinatesMm;
                if (pt) {
                    const dist = vec3.distance(patientCoords, pt);
                    if (dist < threshold && dist < minDist) {
                        result = roi;
                        minDist = dist;
                    }
                }
                return;
            }
            const texture = roi.texture;
            const sdf = texture?.signedDistanceField;
            if (sdf) {
                const sdfCoords =
                    texture.textureGrid.toVoxelSpace(patientCoords);
                const dist = sdf.distanceToContour(sdfCoords);

                if (dist < threshold && dist < minDist) {
                    result = roi;
                    minDist = dist;
                }
            }
        });

        return result;
    }

    public get grid(): Grid {
        if (!this.parent.image) {
            throw new Error('StructureSetObject must have an image');
        }
        return this.parent.image.grid;
    }

    /**
     * Handle ROI property updates from domain events.
     * This specifically handles ROI collection-level operations.
     */
    private handleRoiPropertiesUpdated(event: RoiPropertiesUpdatedEvent): void {
        const properties = event.updatedProperties;

        // Handle ROI deletion
        if (properties.deleted === true) {
            this.deleteRoi(event.roiNumber);
        }

        // TODO: Handle all ROI property updates (now in RegionOfInterest)
    }

    private handleRoiCreated(event: RoiCreatedEvent): void {
        const { structureSetUid, roiNumber } = event;

        if (structureSetUid === this.parent.dicomUid) {
            const roi = this.parent.getRoiByNumber(roiNumber);
            if (!roi) {
                throw new Error(
                    `RoiCreatedEvent received for ROI number ${roiNumber}, but it does not exist in StructureSet ${structureSetUid}`
                );
            }

            const regionOfInterest = new RegionOfInterest(roi, this.grid);
            this.regionsOfInterest.set(roi.roiNumber, regionOfInterest);

            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    regionOfInterest.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    /**
     * Method to erase the array of {@link roi RegionOfInterest}.
     */
    public delete() {
        for (const roi of this.structures) {
            roi.delete();
        }
        this.regionsOfInterest.clear();
        this._eventSubscriptions?.unsubscribeAll();
    }

    private deleteRoi(roiNumber: number): void {
        const roi = this.getRoi(roiNumber);
        if (roi) {
            roi.delete();
            this.regionsOfInterest.delete(roiNumber);
        }
    }
}
